import { FC, ReactNode, useState } from 'react';
import styles from './base-message.module.scss';
import { ThreadMessageAuthor } from '@/models/thread';
import SourcesIcon from '@/assets/icons/database.svg?react';

import { extractBaseURL, truncateText } from '@/utils/functions';
import useSession from '@/hooks/useSession';
import useItem from '@/pages/Thread/hooks/useItem';
import IconHeading from '../../IconHeading';
import SourceIndicator from '../../SourceIndicator';
import Options from '../Options';
import SourceDetailsModal from '../SourceDetailsModal';
import useThread from '@/pages/Thread/hooks/useThread';

interface BaseMessageProps {
  author: ThreadMessageAuthor;
  children: ReactNode;
}

const BaseMessage: FC<BaseMessageProps> = ({ author, children }) => {
  const { pushUserEvent } = useSession();
  const { status } = useThread();
  const { item, isLast } = useItem();
  const { sources } = item;
  const [modalSource, setModalSource] = useState<number | null>(null);

  return (
    <div
      data-testid={`msg-${author}`}
      className={`${styles.message} ${
        author === 'USER' ? styles['user-message'] : styles['assistant-message']
      }`}
      data-loading={
        author === 'USER' ? null : !!(isLast && status === 'FETCHING')
      }
    >
      <div className={styles['message-content']}>{children}</div>
      {author === 'ASSISTANT' && (
        <>
          {(!isLast || status === 'READY' || status === 'CITING') &&
            sources &&
            sources.length > 0 && (
              <div className={styles['message-sources']}>
                <IconHeading>
                  <SourcesIcon /> Sources ({sources.length})
                </IconHeading>
                <ul>
                  {sources?.map(({ url, title, provider, sample }, i) => {
                    const name = url
                      ? extractBaseURL(url)
                      : provider || 'Unknown';
                    const truncatedTitle = truncateText(title, 60);
                    return (
                      <li key={i}>
                        <div>
                          <SourceIndicator id={i + 1} />
                          <a
                            href={url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => {
                              if (sample) {
                                event.preventDefault();
                                setModalSource(i);
                              }
                              pushUserEvent('click_response_source');
                            }}
                          >
                            {truncatedTitle}
                          </a>
                        </div>
                        <span>— {name}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          <Options />
        </>
      )}

      {modalSource !== null && sources?.[modalSource] && (
        <SourceDetailsModal
          source={sources[modalSource]}
          index={modalSource + 1}
          onClose={() => setModalSource(null)}
        />
      )}
    </div>
  );
};

export default BaseMessage;
